import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, Circle, Crown, Users, LogOut, Plus } from "lucide-react";

function ClanTag({ tag }) {
  return (
    <span className="font-numeric text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-800/70 bg-amber-950/40 text-amber-200">
      [{tag}]
    </span>
  );
}

function BrowseRow({ clan, onJoin, disabled }) {
  return (
    <div className="panel-inset p-2.5 flex items-center gap-3" data-testid={`browse-clan-${clan.id}`}>
      <div className="w-10 h-10 rounded bg-stone-950 border border-amber-800/70 flex items-center justify-center">
        <Shield size={16} className="text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm text-stone-100 truncate">{clan.name}</span>
          <ClanTag tag={clan.tag} />
        </div>
        <div className="text-[11px] font-body text-stone-500 truncate italic">
          {clan.description || <span className="opacity-60">No description.</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] font-numeric text-stone-500 flex items-center gap-1">
          <Users size={11} /> {clan.member_count}
        </span>
        <button
          onClick={() => onJoin(clan.id)}
          disabled={disabled}
          className="btn-gold text-[10px] px-2 py-1"
          data-testid={`join-clan-${clan.id}`}
        >
          Join
        </button>
      </div>
    </div>
  );
}

function MyClan({ mine, onLeave, refresh }) {
  const clan = mine.clan;
  return (
    <div className="panel p-4 space-y-3" data-testid="my-clan-card">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded bg-stone-950 border-2 border-amber-700 flex items-center justify-center">
          <Shield size={26} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl heading-gilded truncate">{clan.name}</h3>
            <ClanTag tag={clan.tag} />
          </div>
          <div className="text-[11px] italic text-stone-500 font-body">{clan.description || "No description."}</div>
          <div className="flex flex-wrap gap-3 mt-1 text-[10px] font-numeric text-stone-500">
            <span>Members · <span className="text-amber-300">{clan.member_count}</span></span>
            <span>Founded · <span className="text-amber-300">{(clan.created_at || "").slice(0, 10) || "—"}</span></span>
          </div>
        </div>
        <button
          onClick={onLeave}
          className="btn-ghost-gold text-[10px] px-2 py-1 flex items-center gap-1"
          data-testid="leave-clan-btn"
        >
          <LogOut size={12} /> Leave
        </button>
      </div>

      <div>
        <h4 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-1.5">Members</h4>
        <div className="space-y-1" data-testid="clan-members-list">
          {mine.members.map((m) => (
            <div key={m.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-stone-900/40">
              <div className="w-7 h-7 rounded-full bg-stone-950 border border-amber-800 flex items-center justify-center font-display text-[10px] text-amber-400">
                {(m.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-body text-sm text-stone-100 truncate">{m.name}</span>
                  {m.id === clan.owner_id && (
                    <Crown size={11} className="text-amber-400" />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={
                    "inline-flex items-center gap-1 text-[9px] font-numeric uppercase tracking-widest " +
                    (m.online ? "text-emerald-400" : "text-stone-500")
                  }
                >
                  <Circle size={7} className={m.online ? "fill-emerald-400" : "fill-stone-600"} strokeWidth={0} />
                  {m.online ? "Online" : "Offline"}
                </span>
                <span className="text-[10px] font-numeric text-stone-500">Lv {m.total_level}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CreateClanForm({ onCreated }) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/api/game/social/clans/create", { name: name.trim(), tag: tag.trim(), description: desc.trim() });
      toast.success("Clan founded.");
      setName(""); setTag(""); setDesc("");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="panel p-3 space-y-2" data-testid="create-clan-form">
      <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">Found a clan</h3>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Clan name (3-40 chars)"
        minLength={3} maxLength={40}
        required
        className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 font-body text-sm focus:border-amber-600 outline-none"
        data-testid="clan-name-input"
      />
      <input
        value={tag}
        onChange={(e) => setTag(e.target.value.toUpperCase())}
        placeholder="Tag (2-5 letters/digits, e.g. LIFE)"
        minLength={2} maxLength={5}
        required
        className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 font-numeric text-sm uppercase tracking-widest focus:border-amber-600 outline-none"
        data-testid="clan-tag-input"
      />
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Description (optional, up to 280 chars)"
        maxLength={280}
        rows={2}
        className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 font-body text-sm focus:border-amber-600 outline-none"
        data-testid="clan-desc-input"
      />
      <button type="submit" disabled={busy} className="btn-gold px-3 py-1.5 text-xs w-full flex items-center justify-center gap-1" data-testid="create-clan-btn">
        <Plus size={12} /> Found this clan
      </button>
    </form>
  );
}

export function ClansPanel() {
  const [mine, setMine] = useState(null);
  const [browse, setBrowse] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [m, b] = await Promise.all([
        api.get("/api/game/social/clans/mine"),
        api.get("/api/game/social/clans"),
      ]);
      setMine(m.data);
      setBrowse(b.data.clans);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  async function join(clan_id) {
    try {
      await api.post("/api/game/social/clans/join", { clan_id });
      toast.success("Joined clan!");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function leave() {
    try {
      await api.post("/api/game/social/clans/leave", {});
      toast.message("Left clan.");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const inClan = !!mine?.clan;

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="clans-panel">
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/viewport_house.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-20 h-20 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <Shield size={34} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl heading-gilded">Clans</h1>
            <p className="text-stone-400 font-body italic text-sm">
              Bind yourself to a banner. Share a channel. Build a legend.
            </p>
          </div>
        </div>
      </div>

      {loading && !mine && (
        <div className="panel p-6 text-stone-500 italic text-center">Rallying banners…</div>
      )}

      {inClan ? (
        <MyClan mine={mine} onLeave={leave} refresh={refresh} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-1">
            <CreateClanForm onCreated={refresh} />
          </div>
          <div className="lg:col-span-2 panel p-3">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2">
              Browse clans ({browse.length})
            </h3>
            {browse.length === 0 ? (
              <div className="text-stone-500 italic text-sm text-center py-6">
                No clans have been founded yet. Be the first.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="clans-browse-list">
                {browse.map((c) => (
                  <BrowseRow key={c.id} clan={c} onJoin={join} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
